import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, LockIcon, ZapIcon, CreditCardIcon, StarIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { CTAButton } from './CTAButton';
import { CHECKOUT_ESENCIAL, CHECKOUT_PREMIUM } from '../lib/links';
const plans = [{
  name: 'Mega Pack Esencial',
  emoji: '📘',
  highlight: false,
  tagline: '3 Tomos + 6 Bonos',
  features: ['Tomo 1 — El Escudo Familiar', 'Tomo 2 — Cenas Rápidas y Post-Laburo', 'Tomo 3 — Panadería y Dulces Patrios', 'Los 6 bonos completos'],
  oldPrice: '$60.000',
  price: '$19.990',
  cta: 'Quiero mi Mega Pack',
  variant: 'primary' as const,
  href: CHECKOUT_ESENCIAL
}, {
  name: 'Pack Premium',
  emoji: '⭐',
  highlight: true,
  tagline: '3 Tomos + 6 Bonos + Método Batch Cooking 120',
  note: 'Resolvé el mes entero cocinando solo 2 horas por semana.',
  features: ['Todo lo del Mega Pack Esencial', 'Método Batch Cooking 120', 'Plan mensual de cocina en 2 hs semanales', 'Sistema de freezer ampliado'],
  oldPrice: '$85.000',
  price: '$27.990',
  cta: 'Quiero el Pack Premium',
  variant: 'secondary' as const,
  href: CHECKOUT_PREMIUM
}];
const payment = [{
  icon: LockIcon,
  label: 'Pago seguro'
}, {
  icon: ZapIcon,
  label: 'Acceso en 5 min'
}, {
  icon: CreditCardIcon,
  label: 'Mercado Pago · Tarjeta · Transferencia'
}];
export function Pricing() {
  return <section id="precios" className="w-full bg-cream-deep/60 px-5 py-16 sm:px-8 lg:py-24" data-id="element-126" data-mp="3i93lhq0dtu9j">
      <div className="mx-auto max-w-5xl" data-id="element-127" data-mp="3n6d70uknhls3">
        <SectionHeading eyebrow="Elegí tu pack" title="Empezá hoy, cociná distinto el domingo" data-id="element-128" data-mp="3n5afqljf1hbn" />

        <div className="mt-12 grid items-start gap-6 md:grid-cols-2" data-id="element-129" data-mp="2uizinkv0pyjl">
          {plans.map((plan, i) => <motion.article key={plan.name} initial={{
          opacity: 0,
          y: 26
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.1,
          ease: 'easeOut'
        }} className={`relative flex flex-col rounded-3xl p-7 sm:p-8 ${plan.highlight ? 'border-2 border-clay bg-cream-card shadow-lift' : 'border border-forest/12 bg-cream-card shadow-soft'}`} data-id="element-130" data-mp="2zgr9ebjxfem7" data-mpi={i}>
              {plan.highlight && <span className="absolute -top-3.5 left-7 inline-flex items-center gap-1.5 rounded-full bg-clay px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-white" data-id="element-131" data-mp="23jpdm0zeixii">
                  <StarIcon className="h-3 w-3 fill-white" aria-hidden="true" data-id="element-132" data-mp="2dgk90b0dohsk" />
                  Más completo
                </span>}

              <h3 className="font-display text-2xl text-forest-dark sm:text-[1.75rem]" data-id="element-133" data-mp="3ttihw81tbacg">
                {plan.emoji} {plan.name}
              </h3>
              <p className="mt-2 text-sm font-medium text-forest-dark/60" data-id="element-134" data-mp="anegnzeyitg">
                {plan.tagline}
              </p>
              {plan.note && <p className="mt-3 rounded-xl bg-clay/8 px-4 py-3 text-sm leading-relaxed text-clay" data-id="element-135" data-mp="5aruye0bndjo">
                  {plan.note}
                </p>}

              <ul className="mt-6 space-y-2.5" data-id="element-136" data-mp="3rhq0z24b40xc">
                {plan.features.map(f => <li key={f} className="flex items-start gap-2.5 text-sm text-forest-dark/80" data-id="element-137" data-mp="j1wmestjczix">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-forest" strokeWidth={2.5} aria-hidden="true" data-id="element-138" data-mp="o1stx21fz938" />
                    {f}
                  </li>)}
              </ul>

              <div className="mt-7 border-t border-forest/10 pt-6" data-id="element-139" data-mp="11xmqcll145zg">
                <p className="text-sm text-forest-dark/40 line-through" data-id="element-140" data-mp="16r946h2xifwu">
                  {plan.oldPrice}
                </p>
                <p className="mt-1 font-display text-4xl font-semibold text-forest-dark sm:text-5xl" data-id="element-141" data-mp="3f5flyze76v7p">
                  {plan.price}{' '}
                  <span className="font-sans text-base font-medium text-forest-dark/50" data-id="element-142" data-mp="358t2adxlya79">
                    ARS
                  </span>
                </p>
              </div>

              <div className="mt-6" data-id="element-143" data-mp="1sm8r08wptc7">
                <CTAButton variant={plan.variant} href={plan.href} data-id="element-144" data-mp="1rkktv6slcd2">
                  {plan.cta}
                </CTAButton>
                <p className="mt-3 text-center text-xs text-forest-dark/45" data-id="element-145" data-mp="3szgmnyopmsap">
                  www.nuevonivel.shop
                </p>
              </div>
            </motion.article>)}
        </div>

        <ul className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-forest-dark/60" data-id="element-146" data-mp="3k6d3d9rdvl85">
          {payment.map(({
          icon: Icon,
          label
        }) => <li key={label} className="inline-flex items-center gap-1.5" data-id="element-147" data-mp="frllpan24jsj">
              <Icon className="h-4 w-4 text-forest-light" aria-hidden="true" data-id="element-148" data-mp="1yfofpkdv4s9c" />
              {label}
            </li>)}
        </ul>
      </div>
    </section>;
}